import React, { useCallback, useEffect, useState } from 'react';
import { Keyboard, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { HeadingLevel2 } from '@/components/shared/headline/heading-level-2';
import { Box } from '@/components/shared/layout/box';
import { Card } from '@/components/shared/layout/card';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { PaginationBar } from '@/components/shared/navigation/pagination-bar';
import { keyframes, styled } from '@/stitches.config';
import { IButton } from '@/types/shared';

const contentActiveAnimation = keyframes({
  '0%': { opacity: '0', transform: 'translateX(40px)' },
  '100%': { opacity: '1', transform: 'translateX(0)' },
});

const SlideContainer = styled(Box, {
  '.swiper': {
    paddingBottom: 1,
  },
  '& .swiper-slide': {
    '@maxlg': {
      '.slide-content': {
        opacity: '0',
        transform: 'translateX(40px)',
        animationDuration: '1.2s',
        animationTimingFunction: 'var(--transition-easing-slider)',
        animationDelay: '.4s',
        animationFillMode: 'forwards',
        willChange: 'opacity, transform',
      },
      '&.swiper-slide-active': {
        '.slide-content': {
          animationName: `${contentActiveAnimation}`,
        },
      },
    },
  },
  variants: {
    type: {
      loop: {
        '& .swiper': {
          '@lg': {
            overflow: 'inherit',
          },
          '@2xl': {
            overflow: 'hidden',
          },
        },
        '& .swiper-slide': {
          '&:not(.one-item)': {
            '@maxlg': {
              width: 'calc(100% - 96px - 48px) !important',
            },
            '@maxsm': {
              width: 'calc(100% - 48px - 32px) !important',
            },
          },
          '&.one-item': {
            '@maxmd': {
              width: '100% !important',
            },
          },
        },
      },
      'no-loop': {
        '& .swiper': {
          px: '$space-6',
          '@sm': {
            px: '$space-12',
          },
          '@lg': {
            px: '$space-0',
          },
        },
        '& .swiper-slide': {
          '&:not(.one-item)': {
            '@maxlg': {
              width: 'calc(100% - 24px) !important',
            },
            '@maxsm': {
              width: 'calc(100% - 16px) !important',
            },
          },
          '&.one-item': {
            '@maxmd': {
              width: '100% !important',
            },
          },
        },
      },
    },
  },
  defaultVariants: {
    type: 'loop',
  },
});

const ButtonIconWrap = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$space-4',
  marginTop: '$space-8',
  '@lg': {
    marginTop: '$space-12',
  },
  '@2xl': {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '0',
    marginTop: '0',
    paddingTop: 'calc(219 / 1232 * 100%)',
    '& button': {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      '&.slide-prev': {
        right: '100%',
        marginRight: '$space-6',
      },
      '&.slide-next': {
        left: '100%',
        marginLeft: '$space-6',
      },
    },
  },
  '@max2xl': {
    display: 'none',
  },
});

export interface IOffersCTA {
  dataTestSection?: string;
  dataTest?: string;
  dataTrack?: string;
  background?: string | 'white' | 'gray' | 'dark';
  lead?: string;
  title: string;
  buttons?: IButton[];
  loop?: boolean;
  items?: {
    image?: ImageProps;
    title: string;
    description?: string;
    button?: IButton;
  }[];
}

const View: React.FC<IOffersCTA> = ({
  background = 'white',
  items = [],
  loop = true,
  dataTestSection = 'section_specialOfferCta',
  dataTest = 'specialOfferCta',
  dataTrack = 'offers-cta',
  ...props
}) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [left, setLeft] = useState<boolean>(true);
  const [right, setRight] = useState<boolean>(true);

  const onPrev = () => {
    swiper?.slidePrev();
  };

  const onNext = () => {
    swiper?.slideNext();
  };

  const onSlideChangeUpdate = useCallback(() => {
    if (!swiper) return;
    if (loop) return;

    setLeft(!swiper.isBeginning);
    setRight(!swiper.isEnd);
  }, [swiper, loop]);

  useEffect(() => {
    onSlideChangeUpdate();
  }, [onSlideChangeUpdate]);

  const bgVariant = () => {
    switch (background) {
      case 'dark':
        return 'gradient-gray-dark';
      case 'gray':
        return 'gray-light';
      case 'white':
        return 'white';
    }
  };

  const arrowVariant = () => {
    switch (background) {
      case 'dark':
        return 'button-icon-color-white';
      case 'gray':
        return 'button-icon-color-gray-dark';
      case 'white':
        return 'button-icon-color-gray-dark';
    }
  };

  const paginationVariant = () => {
    switch (background) {
      case 'dark':
        return 'white';
      case 'gray':
      case 'white':
        return 'black';
    }
  };

  return (
    <Section
      size="xl"
      backgroundColor={bgVariant()}
      test={dataTestSection}
      css={{ marginBottom: -1 }}
    >
      <HeadingLevel2
        lead={props.lead}
        title={props.title}
        buttons={props.buttons}
        isDarkTheme={background === 'dark'}
        hideButtonOnMobile
        dataTrack={dataTrack}
      />
      {items.length > 0 && (
        <>
          <SlideContainer
            position="relative"
            width="full"
            overflow="hidden"
            type={loop ? 'loop' : 'no-loop'}
            className={loop ? 'is-loop' : undefined}
          >
            <Container size="desktop-only">
              <Box position="relative" width="full">
                {items.length === 1 && (
                  <Swiper
                    modules={[Keyboard]}
                    keyboard
                    speed={1000}
                    slidesPerView={'auto'}
                    centeredSlides={false}
                    spaceBetween={16}
                    loop={false}
                    allowTouchMove={false}
                    onSlideChange={onSlideChangeUpdate}
                    breakpoints={{
                      '640': {
                        slidesPerView: 'auto',
                        spaceBetween: 24,
                        centeredSlides: false,
                        allowTouchMove: false,
                      },
                      '768': {
                        slidesPerView: 2,
                        spaceBetween: 24,
                        centeredSlides: false,
                        allowTouchMove: false,
                      },
                      '1024': {
                        slidesPerView: 3,
                        spaceBetween: 32,
                        centeredSlides: false,
                        allowTouchMove: false,
                      },
                    }}
                    onSwiper={setSwiper}
                  >
                    {items.map((offer, index) => (
                      <SwiperSlide
                        key={index}
                        className={items.length === 1 ? 'one-item' : undefined}
                      >
                        <Card
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-ignore
                          image={offer.image}
                          title={offer.title}
                          button={offer.button}
                          variant={
                            background === 'dark' ? 'white' : 'gray-darkest'
                          }
                          tracking={{
                            dataTrack: dataTrack,
                            dataTrackText: offer.button?.label,
                            dataTrackUrl: offer.button?.url,
                            dataTrackValue: offer.title,
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
                {items.length > 1 && (
                  <>
                    <Swiper
                      modules={[Keyboard, Pagination]}
                      keyboard
                      speed={1000}
                      slidesPerView={'auto'}
                      centeredSlides={loop}
                      spaceBetween={16}
                      loop={loop}
                      allowTouchMove
                      onSlideChange={onSlideChangeUpdate}
                      breakpoints={{
                        '640': {
                          slidesPerView: 'auto',
                          spaceBetween: 24,
                          centeredSlides: loop ? true : false,
                          allowTouchMove: true,
                        },
                        '1024': {
                          slidesPerView: 3,
                          spaceBetween: 32,
                          centeredSlides: false,
                          allowTouchMove: items.length > 3 ? true : false,
                        },
                      }}
                      pagination={{
                        el: `.swiper-pagination-offers-cta`,
                        clickable: true,
                        renderBullet: function (index, className) {
                          return `<div class="${className}" data-test="pagination_${
                            index + 1
                          }"><span>${index + 1}</span></div>`;
                        },
                      }}
                      onSwiper={setSwiper}
                    >
                      {items.map((offer, index) => (
                        <SwiperSlide key={index}>
                          <Card
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            image={{
                              ...offer.image,
                              sources: [
                                {
                                  srcset: offer.image?.src as string,
                                  media: { minWidth: 1024 },
                                  options: { width: 414, crop: '16:9' },
                                },
                                ...generateTabletAndMobileImageSources({
                                  mobileSrc: offer.image?.src,
                                  mobileOptions: {
                                    crop: '16:9',
                                  },
                                }),
                              ],
                            }}
                            title={offer.title}
                            button={offer.button}
                            variant={
                              background === 'dark' ? 'white' : 'gray-darkest'
                            }
                            tracking={{
                              dataTrack: dataTrack,
                              dataTrackText: offer.button?.label,
                              dataTrackUrl: offer.button?.url,
                              dataTrackValue: offer.title,
                            }}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <ButtonIconWrap>
                      {!!left && (
                        <ButtonIcon
                          icon="arrow-back"
                          size="xl"
                          variant={arrowVariant()}
                          onClick={onPrev}
                          className="slide-prev"
                          test={{
                            dataTest: `${dataTest}_button_previous`,
                          }}
                        />
                      )}
                      {!!right && (
                        <ButtonIcon
                          icon="arrow-forward"
                          size="xl"
                          variant={arrowVariant()}
                          onClick={onNext}
                          className="slide-next"
                          test={{
                            dataTest: `${dataTest}_button_next`,
                          }}
                        />
                      )}
                    </ButtonIconWrap>
                  </>
                )}
              </Box>
            </Container>
          </SlideContainer>
          {items.length > 1 && (
            <Container size="desktop-only">
              <PaginationBar
                variant={paginationVariant()}
                css={{
                  marginTop: '$space-6',
                  '@lg': {
                    marginTop: '$space-12',
                  },
                }}
              >
                <div
                  className="swiper-pagination swiper-pagination-offers-cta"
                  data-test="paginations"
                />
              </PaginationBar>
            </Container>
          )}
        </>
      )}
      <HeadingLevel2
        lead={props.lead}
        title={props.title}
        buttons={props.buttons}
        isDarkTheme={background === 'dark'}
        hideLeadAndTitle
        dataTrack={dataTrack}
      />
    </Section>
  );
};
View.displayName = 'SectionSharedOffersCTA';
export default View;
